import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { CheckCircle, Star, Clock, Smile, Shield, CreditCard, Award, Users } from 'lucide-react';

const InvisalignCost = () => {
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "Why choose Fort Worth Dental for Invisalign?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Fort Worth Dental is a premier Invisalign provider with experienced doctors who have transformed thousands of smiles. We offer personalized treatment plans, advanced 3D imaging technology, and flexible financing options to make Invisalign affordable for every budget."
        }
      },
      {
        "@type": "Question",
        "name": "Is Invisalign covered by insurance?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Many dental insurance plans cover Invisalign similarly to traditional braces. Fort Worth Dental accepts most insurance plans and can help you understand and maximize your coverage."
        }
      },
      {
        "@type": "Question",
        "name": "Does Fort Worth Dental offer Invisalign payment plans?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes! We offer multiple financing options including CareCredit with 0% interest plans, as well as flexible in-house payment arrangements to fit your budget."
        }
      },
      {
        "@type": "Question",
        "name": "How long does Invisalign treatment take?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Treatment time varies based on your individual needs, typically ranging from 6-18 months. During your FREE consultation, we'll provide a personalized timeline for your smile transformation."
        }
      }
    ]
  };

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Invisalign Fort Worth TX | Premier Clear Aligner Provider | Fort Worth Dental</title>
        <meta name="description" content="Fort Worth Dental is your premier Invisalign provider. FREE consultation with 3D smile preview. Flexible financing options available. Expert care from Dr. Razo. Call (817) 985-3337." />
        <meta name="keywords" content="invisalign fort worth, invisalign provider fort worth tx, clear aligners, invisalign financing, best invisalign dentist fort worth" />
        <link rel="canonical" href="https://fortworthdental.com/invisalign-cost-fort-worth" />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-purple-700 via-purple-600 to-purple-800 text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl">
            <p className="text-purple-200 font-semibold mb-2 flex items-center gap-2">
              <Award className="w-5 h-5" /> Premier Invisalign Provider in Fort Worth
            </p>
            <h1 className="text-4xl sm:text-5xl font-bold mb-6">
              Your Perfect Smile Starts Here
            </h1>
            <p className="text-xl text-purple-100 mb-8">
              Fort Worth Dental combines expert Invisalign treatment with flexible financing options 
              to make your dream smile a reality. Experience the difference of working with 
              Fort Worth's trusted clear aligner specialists.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link 
                to="/contact-us" 
                className="bg-white text-purple-700 px-8 py-4 rounded-lg font-bold text-center hover:bg-gray-100 transition-colors"
                data-testid="hero-cta-contact"
              >
                Schedule FREE Consultation
              </Link>
              <Link 
                to="/invisalign" 
                className="border-2 border-white text-white px-8 py-4 rounded-lg font-bold text-center hover:bg-white/10 transition-colors"
              >
                Learn About Invisalign
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Why Fort Worth Dental */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl font-bold text-gray-800 mb-4">
              Why Fort Worth Dental is Your Best Choice for Invisalign
            </h2>
            <p className="text-gray-600 text-lg">
              As a premier Invisalign provider, we've helped thousands of patients achieve 
              their perfect smile with personalized care and affordable solutions.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            <div className="text-center p-6">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 rounded-full mb-4">
                <Award className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="font-bold text-gray-800 mb-2">Certified Experts</h3>
              <p className="text-gray-600 text-sm">
                Our doctors have extensive Invisalign training and experience with all case types
              </p>
            </div>
            <div className="text-center p-6">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 rounded-full mb-4">
                <Smile className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="font-bold text-gray-800 mb-2">3D Smile Preview</h3>
              <p className="text-gray-600 text-sm">
                See your future smile before starting treatment with our advanced imaging
              </p>
            </div>
            <div className="text-center p-6">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 rounded-full mb-4">
                <CreditCard className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="font-bold text-gray-800 mb-2">Flexible Financing</h3>
              <p className="text-gray-600 text-sm">
                Multiple payment options to fit any budget with affordable monthly plans
              </p>
            </div>
            <div className="text-center p-6">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 rounded-full mb-4">
                <Users className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="font-bold text-gray-800 mb-2">Personalized Care</h3>
              <p className="text-gray-600 text-sm">
                Customized treatment plans designed for your unique smile goals
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Invisalign Benefits vs Traditional Braces */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-gray-800 mb-4 text-center">
            Invisalign vs. Traditional Braces
          </h2>
          <p className="text-gray-600 text-center mb-8 max-w-2xl mx-auto">
            Discover why more patients are choosing Invisalign for their smile transformation
          </p>
          
          <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-lg overflow-hidden">
            <table className="w-full">
              <thead className="bg-purple-600 text-white">
                <tr>
                  <th className="px-6 py-4 text-left font-semibold">Benefit</th>
                  <th className="px-6 py-4 text-center font-semibold">Invisalign</th>
                  <th className="px-6 py-4 text-center font-semibold">Metal Braces</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-medium text-gray-800">Appearance</td>
                  <td className="px-6 py-4 text-center">
                    <span className="text-green-600 font-semibold">Nearly invisible</span>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <span className="text-red-500">Visible metal</span>
                  </td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-medium text-gray-800">Removable</td>
                  <td className="px-6 py-4 text-center">
                    <CheckCircle className="w-5 h-5 text-green-600 mx-auto" />
                  </td>
                  <td className="px-6 py-4 text-center">
                    <span className="text-red-500 text-xl">✗</span>
                  </td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-medium text-gray-800">Eating Restrictions</td>
                  <td className="px-6 py-4 text-center">
                    <span className="text-green-600 font-semibold">None</span>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <span className="text-red-500">Many foods restricted</span>
                  </td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-medium text-gray-800">Comfort Level</td>
                  <td className="px-6 py-4 text-center">
                    <span className="text-green-600 font-semibold">Smooth & comfortable</span>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <span className="text-red-500">Can irritate cheeks</span>
                  </td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-medium text-gray-800">Office Visits</td>
                  <td className="px-6 py-4 text-center">
                    <span className="text-green-600 font-semibold">50% fewer visits</span>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <span className="text-gray-600">Monthly adjustments</span>
                  </td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-medium text-gray-800">Oral Hygiene</td>
                  <td className="px-6 py-4 text-center">
                    <span className="text-green-600 font-semibold">Easy to maintain</span>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <span className="text-red-500">Difficult cleaning</span>
                  </td>
                </tr>
                <tr className="hover:bg-gray-50 bg-purple-50">
                  <td className="px-6 py-4 font-medium text-gray-800">Time Savings</td>
                  <td className="px-6 py-4 text-center">
                    <span className="text-green-600 font-bold text-lg">Up to 50% faster*</span>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <span className="text-gray-600">Standard timeline</span>
                  </td>
                </tr>
              </tbody>
            </table>
            <p className="text-xs text-gray-500 px-6 py-3 bg-gray-50">
              *Treatment time varies based on individual cases. Consult with our team for your personalized timeline.
            </p>
          </div>
        </div>
      </section>

      {/* Financing Options */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-800 mb-4">
                Affordable Financing Options
              </h2>
              <p className="text-gray-600 text-lg">
                At Fort Worth Dental, we believe everyone deserves a beautiful smile. 
                That's why we offer flexible payment solutions to fit your budget.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-gradient-to-br from-blue-600 to-blue-700 text-white p-8 rounded-xl shadow-lg">
                <h3 className="text-2xl font-bold mb-4 flex items-center gap-2">
                  <CreditCard className="w-6 h-6" /> CareCredit Financing
                </h3>
                <ul className="space-y-3">
                  <li className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-blue-200 flex-shrink-0" />
                    <span>0% interest promotional periods available</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-blue-200 flex-shrink-0" />
                    <span>Low monthly payments</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-blue-200 flex-shrink-0" />
                    <span>Quick and easy approval process</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-blue-200 flex-shrink-0" />
                    <span>No prepayment penalties</span>
                  </li>
                </ul>
              </div>
              
              <div className="bg-gray-100 p-8 rounded-xl">
                <h3 className="text-2xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                  <Shield className="w-6 h-6 text-gray-700" /> Insurance & More
                </h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                    <span>Most dental insurance accepted</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                    <span>HSA/FSA funds welcome</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                    <span>In-house payment plans available</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                    <span>We verify your benefits for you</span>
                  </li>
                </ul>
              </div>
            </div>
            
            <div className="mt-8 text-center">
              <Link 
                to="/contact-us" 
                className="inline-flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white px-8 py-4 rounded-lg font-bold transition-colors"
                data-testid="financing-cta-contact"
              >
                Discuss Financing Options
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-3xl font-bold text-gray-800 mb-8 text-center">
            Frequently Asked Questions
          </h2>
          <div className="space-y-4">
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
              <h3 className="font-bold text-gray-800 mb-2">
                Why choose Fort Worth Dental for Invisalign?
              </h3>
              <p className="text-gray-600">
                Fort Worth Dental is a premier Invisalign provider with experienced doctors who have 
                transformed thousands of smiles. We offer personalized treatment plans, advanced 3D 
                imaging technology, and flexible financing options to make Invisalign affordable for every budget.
              </p>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
              <h3 className="font-bold text-gray-800 mb-2">
                Is Invisalign covered by insurance?
              </h3>
              <p className="text-gray-600">
                Many dental insurance plans cover Invisalign similarly to traditional braces. 
                Fort Worth Dental accepts most insurance plans and can help you understand 
                and maximize your coverage during your FREE consultation.
              </p>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
              <h3 className="font-bold text-gray-800 mb-2">
                What financing options are available?
              </h3>
              <p className="text-gray-600">
                We offer multiple financing options including CareCredit with 0% interest promotional 
                periods, as well as flexible in-house payment arrangements. Our team will work with 
                you to find a solution that fits your budget.
              </p>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
              <h3 className="font-bold text-gray-800 mb-2">
                How long does Invisalign treatment take?
              </h3>
              <p className="text-gray-600">
                Treatment time varies based on your individual needs, typically ranging from 6-18 months. 
                Many patients see results faster than traditional braces. During your FREE consultation, 
                we'll provide a personalized timeline for your smile transformation.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 bg-gradient-to-r from-purple-700 to-purple-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">
            Ready to Transform Your Smile?
          </h2>
          <p className="text-xl text-purple-100 mb-8 max-w-2xl mx-auto">
            Schedule your FREE consultation today and see your future smile with our advanced 3D imaging technology.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/contact-us" 
              className="bg-white text-purple-700 px-8 py-4 rounded-lg font-bold text-lg hover:bg-gray-100 transition-colors inline-block"
              data-testid="final-cta-contact"
            >
              Book Your FREE Consultation
            </Link>
            <a 
              href="tel:8179853337" 
              className="border-2 border-white text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-white/10 transition-colors inline-block"
            >
              Call (817) 985-3337
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default InvisalignCost;
